// frontend/src/pages/UsersPage.jsx
import dayjs from 'dayjs';

import React, { useState, useEffect, useMemo } from 'react';
import { useData } from '../hooks/useData.js';
import { apiFetch } from '../api/index.js';
import { LoadingSpinner, StandardPageLayout, Button, Modal, ConfirmModal, FormInput, PasswordInput, FormSelect, Avatar } from '../components/Common.jsx';
import { PlusCircle } from 'lucide-react';

import { Box, TextField, InputAdornment, MenuItem, Select, FormControl, InputLabel, Stack } from "@mui/material";
import { Search as SearchIcon } from '@mui/icons-material';
import PasswordStrengthIndicator from '../components/PasswordStrengthIndicator.jsx';
import { validatePassword } from '../utils/passwordValidation';
import { hasPermission, isTenantScoped } from '../utils/permissions';
import { PERMISSIONS } from '../constants/permissions';
import { useTimezoneFormatter } from '../hooks/useTimezoneFormatter';
import { notify } from '@/components/core';

const UserForm = ({
  onSave,
  onCancel,
  user,
  roles,
  tenants,
  partners,
  me,
  policyCache,
  setPolicyCache
}) => {
  const [passwordPolicy, setPasswordPolicy] = useState(null);
  const fetchIdRef = React.useRef(0);
  const isPartnerAdmin = hasPermission(me?.user, 'partners:manage') && !hasPermission(me?.user, 'tenants:manage_all');
  const isTenantAdmin = isTenantScoped(me?.user) && hasPermission(me?.user, 'users:manage');
  const isPlatformAdmin = hasPermission(me?.user, 'tenants:manage_all');
  const partnerIdForUser = me?.user?.parent_id || (isPartnerAdmin ? me?.user?.tenantId : null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    roleId: '',
    tenantId: null,
    partnerId: null,
    status: 'Active',
    password: ''
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        roleId: user.roleId || user.role || '',
        tenantId: user.tenantId ?? null,
        partnerId: user.partnerId ?? null,
        status: user.status || 'Active',
        password: ''
      });
    } else {
      setFormData({
        name: '',
        email: '',
        roleId: '',
        tenantId: isTenantAdmin ? me?.user?.tenantId ?? null : null,
        partnerId: isPartnerAdmin ? (partnerIdForUser ?? null) : null,
        status: 'Active',
        password: ''
      });
    }
  }, [user, me, isPartnerAdmin, isTenantAdmin]);

  // Enterprise Password Policy Fetching with Caching & Race-Condition Protection
  useEffect(() => {
    const targetTenantId = formData.tenantId || (isTenantAdmin ? me?.tenantId : null);
    const targetUserId = user?.id;

    const fetchPolicy = async () => {
      const currentFetchId = ++fetchIdRef.current;

      // 1. Check Cache first
      const cacheKey = targetUserId ? `u_${targetUserId}` : `t_${targetTenantId || 'platform'}`;
      const cached = policyCache[cacheKey];

      // TTL Check (5 minutes)
      const now = dayjs().valueOf();
      if (cached && (now - cached.timestamp < 300000)) {
        setPasswordPolicy(cached.policy);
        return;
      }

      try {
        let url = '/auth/password-policy?';
        if (targetUserId) url += `user_id=${targetUserId}`;
        else url += `tenant_id=${targetTenantId || 'platform'}`;

        const res = await apiFetch(url);

        // Race condition check
        if (currentFetchId !== fetchIdRef.current) return;

        const newCacheEntry = { policy: res, timestamp: now };
        setPolicyCache(prev => ({ ...prev, [cacheKey]: newCacheEntry }));
        setPasswordPolicy(res);

      } catch (err) {
        console.error("Failed to fetch password policy", err);
      }
    };

    fetchPolicy();
  }, [formData.tenantId, user?.id, isTenantAdmin, me?.tenantId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validation = React.useMemo(() => validatePassword(formData.password, passwordPolicy), [formData.password, passwordPolicy]);

  const [showWarning, setShowWarning] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();

    // -- Domain checking logic --
    // Only verify if they are being added to a specific tenant
    const selectedTenantId = formData.tenantId || (isTenantAdmin ? me?.user?.tenantId : null);
    if (selectedTenantId && formData.email) {
      const selectedTenant = tenants?.find((t) => t.id === selectedTenantId);
      const tenantDomain = selectedTenant?.domain || (selectedTenant?.adminEmail ? selectedTenant.adminEmail.split('@')[1] : null) || (isTenantAdmin ? me?.user?.email?.split('@')[1] : null);
      const newUserDomain = formData.email.split('@')[1];

      const isEmailChanged = user ? formData.email !== user.email : true;
      if (isEmailChanged && tenantDomain && newUserDomain && tenantDomain !== newUserDomain && !showWarning) {
        setShowWarning(true);
        return;
      }
    }

    processSubmit();
  };

  const processSubmit = () => {
    // --- Mandatory Safety Net: validate on submit regardless of button state ---
    const isNewUser = !user;
    const passwordEntered = formData.password.length > 0;

    if (isNewUser) {
      // New user: password is required
      if (!passwordEntered) {
        notify.error("Password does not meet the required criteria.");
        return;
      }
      if (!validation.isValid) {
        notify.error("Password does not meet the required criteria.");
        return;
      }
    } else {
      // Edit user: only validate if a new password was entered
      if (passwordEntered && !validation.isValid) {
        notify.error("Password does not meet the required criteria.");
        return;
      }
    }

    console.log('[Password Validation Check] Button Disabled: NO — submit proceeding');

    const payload = { ...formData };

    if (isPartnerAdmin) payload.partnerId = partnerIdForUser ?? null;
    if (isTenantAdmin) {
      payload.partnerId = me?.user?.parent_id ?? partnerIdForUser ?? payload.partnerId ?? null;
      payload.tenantId = me?.user?.tenantId ?? null;
    }

    if (!payload.partnerId) payload.partnerId = null;
    if (!payload.tenantId) {
      if (payload.roleId === 'r1' || payload.roleId === 'r2') {
        payload.tenantId = 'platform';
      } else {
        payload.tenantId = null;
      }
    }
    if (user && !payload.password) delete payload.password;

    onSave(payload);
    setShowWarning(false);
  };

  const partnerName = partners?.find((p) => p.id === (formData.partnerId || partnerIdForUser))?.name || '-';
  const tenantName = tenants?.find((t) => t.id === (formData.tenantId || me?.user?.tenantId))?.name || '-';

  return (
    <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
      {/* Dummy hidden inputs to intercept browser autofill */}
      <input type="text" style={{ display: 'none' }} name="fake-email" />
      <input type="password" style={{ display: 'none' }} name="fake-password" />

      <FormInput label="Name" name="name" value={formData.name} onChange={handleChange} required autoComplete="off" />
      <FormInput label="Email" name="email" type="email" value={formData.email} onChange={handleChange} required autoComplete="off" />

      <FormSelect label="Role" name="roleId" value={formData.roleId} onChange={handleChange} required>
        <option value="" disabled>Select role</option>
        {roles?.map((r) => (
          <option key={r.id} value={r.id}>{r.name}</option>
        ))}
      </FormSelect>

      {isPartnerAdmin || isTenantAdmin ? (
        <>
          <div className="text-sm text-gray-700">
            <span className="font-medium">Partner: </span>{partnerName}
          </div>
          <input type="hidden" name="partnerId" value={formData.partnerId || me?.partnerId || ''} />
        </>
      ) : (
        <FormSelect label="Partner" name="partnerId" value={formData.partnerId || ''} onChange={handleChange}>
          <option value="">(none)</option>
          {partners?.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </FormSelect>
      )}

      {isTenantAdmin ? (
        <>
          <div className="text-sm text-gray-700">
            <span className="font-medium">Tenant: </span>{tenantName}
          </div>
          <input type="hidden" name="tenantId" value={formData.tenantId || me?.tenantId || ''} />
        </>
      ) : (
        <FormSelect label="Tenant" name="tenantId" value={formData.tenantId || ''} onChange={handleChange}>
          <option value="">(none)</option>
          {tenants?.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </FormSelect>
      )}

      <FormSelect label="Status" name="status" value={formData.status} onChange={handleChange} required>
        <option>Active</option>
        <option>Inactive</option>
      </FormSelect>

      {!user ? (
        <>
          <PasswordInput label="Password" name="password" value={formData.password} onChange={handleChange} required autoComplete="new-password" />
          <PasswordStrengthIndicator password={formData.password} policy={passwordPolicy} />
        </>
      ) : (
        <>
          <PasswordInput label="Password (leave blank to keep unchanged)" name="password" value={formData.password} onChange={handleChange} autoComplete="new-password" />
          {formData.password && <PasswordStrengthIndicator password={formData.password} policy={passwordPolicy} />}
        </>
      )}

      <div className="flex justify-end gap-4 pt-4">
        <Button type="button" variant="secondary" onClick={onCancel} withShadow>Cancel</Button>
        {/* New user: disabled if empty OR invalid. Edit user: disabled only if entered AND invalid */}
        <Button
          type="submit"
          disabled={!user
            ? (!formData.password || !validation.isValid)   // new user: require valid password
            : (formData.password.length > 0 && !validation.isValid) // edit: optional but must be valid if entered
          }
        >
          {user ? 'Save Changes' : 'Create User'}
        </Button>
      </div>

      <ConfirmModal
        isOpen={showWarning}
        onClose={() => setShowWarning(false)}
        onConfirm={processSubmit}
        title="External Domain Warning"
      >
        The email domain "<strong>{formData.email.split('@')[1]}</strong>" does not match the target tenant's domain.
        <br /><br />
        Are you sure you want to add this external user to the tenant?
      </ConfirmModal>
    </form>
  );
};

const UsersPage = () => {
  const { data: me } = useData('/users/me');
  const { data: users, isLoading, error, refresh } = useData('/users/');
  const { data: roles } = useData('/roles/');
  const { data: tenantsAll } = useData('/tenants/');
  const { data: partnersAll } = useData('/partners/');

  const { formatDate } = useTimezoneFormatter();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [policyCache, setPolicyCache] = useState({});

  // Filter Logic
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [sortOption, setSortOption] = useState('updated_desc');

  const roleId = me?.user?.roleId;
  const rawTid = me?.user?.tenantId;
  const partnerIdForUser = rawTid;

  const isPlatformAdmin = hasPermission(me?.user, 'tenants:manage_all');
  const isPartnerAdmin = hasPermission(me?.user, 'partners:manage') && !hasPermission(me?.user, 'tenants:manage_all');
  const isTenantAdmin = isTenantScoped(me?.user) && hasPermission(me?.user, 'users:manage');

  const partnersScoped = isPartnerAdmin || isTenantAdmin
    ? partnersAll?.filter((p) => p.id === (partnerIdForUser ?? me?.user?.parent_id)) ?? []
    : partnersAll ?? [];

  const tenantsScoped = isTenantAdmin
    ? tenantsAll?.filter((t) => t.id === me?.user?.tenantId) ?? []
    : isPartnerAdmin
      ? tenantsAll?.filter((t) => t.parent_id === (partnerIdForUser ?? me?.user?.parent_id)) ?? []
      : tenantsAll ?? [];

  const openCreate = () => {
    setEditingUser(null);
    setIsModalOpen(true);
  };

  const openManage = (user) => {
    setEditingUser(user);
    setIsModalOpen(true);
  };

  const handleSave = async (payload) => {
    try {
      if (editingUser) {
        await apiFetch(`/users/${editingUser.id}`, null, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      } else {
        await apiFetch('/users/', null, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      }
      setIsModalOpen(false);
      setEditingUser(null);
      refresh();
      notify.success("User saved successfully");
    } catch (e) {
      console.error(e);
      notify.error(e?.message || "Failed to save user");
    }
  };

  const renderStatus = (status) => {
    const isActive = status === 'Active';
    return (
      <span
        className={`px-3 py-1 rounded-full text-sm font-medium ${isActive ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'
          }`}
      >
        {status || '-'}
      </span>
    );
  };

  const renderRole = (roleVal) => {
    const foundRole = roles?.find(r => r.id === roleVal || r.name === roleVal);
    const roleName = foundRole?.name || roleVal || '-';
    const rId = foundRole?.id || roleVal || '';

    const r = (rId || roleName || '').toLowerCase();
    let classes = 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300 border-gray-200/60 dark:border-gray-700';
    if (r === 'r1' || r.includes('super admin')) {
      classes = 'bg-purple-100 text-purple-800 dark:bg-purple-950/40 dark:text-purple-300 border-purple-200/50 dark:border-purple-800/60';
    } else if (r === 'r2' || r.includes('platform admin')) {
      classes = 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-300 border-indigo-200/50 dark:border-indigo-800/60';
    } else if (r === 'r3' || r.includes('partner admin')) {
      classes = 'bg-blue-105 text-blue-850 dark:bg-blue-950/40 dark:text-blue-300 border-blue-200/50 dark:border-blue-800/60';
    } else if (r === 'r4' || r.includes('tenant admin')) {
      classes = 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300 border-emerald-200/50 dark:border-emerald-800/60';
    } else if (r === 'r5' || r.includes('support admin')) {
      classes = 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 border-amber-200/50 dark:border-amber-800/60';
    } else if (r === 'r6' || r.includes('app creator')) {
      classes = 'bg-pink-100 text-pink-805 dark:bg-pink-950/40 dark:text-pink-300 border-pink-200/50 dark:border-pink-800/60';
    } else if (r === 'r7' || r.includes('tenant user')) {
      classes = 'bg-teal-100 text-teal-800 dark:bg-teal-950/40 dark:text-teal-300 border-teal-200/50 dark:border-teal-800/60';
    } else if (r === 'r8' || r === 'r9' || r === 'r10' || r.includes('agent')) {
      classes = 'bg-orange-100 text-orange-800 dark:bg-orange-950/40 dark:text-orange-300 border-orange-200/50 dark:border-orange-800/60';
    }

    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold border tracking-wide uppercase leading-normal shadow-[0_1px_2px_rgba(0,0,0,0.02)] transition-all duration-200 ${classes}`}>
        {roleName}
      </span>
    );
  };

  const canCreate = hasPermission(me?.user, PERMISSIONS.USERS_CREATE);
  const canManage = hasPermission(me?.user, PERMISSIONS.USERS_MANAGE);

  const headerActions = canCreate ? (
    <Button onClick={openCreate} sx={{ height: 32 }}>
      <PlusCircle className="mr-2 h-4 w-4" />
      ADD USER
    </Button>
  ) : null;

  const filteredUsers = (users || []).filter(u => {
    const lowerSearch = searchTerm.toLowerCase();
    const matchesSearch = u.name.toLowerCase().includes(lowerSearch) ||
      u.email.toLowerCase().includes(lowerSearch);

    const roleName = u.roleName || (roles?.find(r => r.id === (u.roleId || u.role))?.name) || '';
    const matchesRole = roleFilter === 'All' || roleName === roleFilter;

    const matchesStatus = statusFilter === 'All' || u.status === statusFilter;

    return matchesSearch && matchesRole && matchesStatus;
  });

  const sortedUsers = useMemo(() => {
    const list = [...filteredUsers];
    list.sort((a, b) => {
      const dateA = new Date(a.updatedAt || a.updated_at || a.createdAt || a.created_at || 0).getTime();
      const dateB = new Date(b.updatedAt || b.updated_at || b.createdAt || b.created_at || 0).getTime();
      if (sortOption === 'updated_desc') return dateB - dateA;
      if (sortOption === 'updated_asc') return dateA - dateB;
      if (sortOption === 'name_asc') return (a.name || '').localeCompare(b.name || '');
      if (sortOption === 'name_desc') return (b.name || '').localeCompare(a.name || '');
      return 0;
    });
    return list;
  }, [filteredUsers, sortOption]);

  if (isLoading) return <LoadingSpinner />;
  if (error)
    return (
      <div className="p-4 text-red-700 bg-red-100 rounded-md">
        Error loading users: {error.message}
      </div>
    );

  const filterControls = (
    <Stack direction="row" spacing={2} alignItems="center">
      <TextField
        size="small"
        placeholder="Search..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
        sx={{ width: 200, bgcolor: 'background.paper', borderRadius: 1 }}

      />
      <FormControl size="small" sx={{ minWidth: 120, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          displayEmpty
        >
          <MenuItem value="All">All Roles</MenuItem>
          {roles?.map(r => <MenuItem key={r.id} value={r.name}>{r.name}</MenuItem>)}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 100, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          displayEmpty
        >
          <MenuItem value="All">All Status</MenuItem>
          <MenuItem value="Active">Active</MenuItem>
          <MenuItem value="Inactive">Inactive</MenuItem>
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 170, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Select
          value={sortOption}
          onChange={(e) => setSortOption(e.target.value)}
          displayEmpty
        >
          <MenuItem value="updated_desc">Recently Updated</MenuItem>
          <MenuItem value="updated_asc">Oldest Updated</MenuItem>
          <MenuItem value="name_asc">Name A-Z</MenuItem>
          <MenuItem value="name_desc">Name Z-A</MenuItem>
        </Select>
      </FormControl>
    </Stack>
  );

  return (
    <StandardPageLayout title="Users" description="Manage user access and permissions." headerActions={headerActions} filterControls={filterControls}>
      <Box className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">User</th>
              <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Email</th>
              <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Role</th>
              <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Partner</th>
              <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Tenant</th>
              <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Joined</th>
              <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
              <th className="p-4 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {sortedUsers.map((u) => (
              <tr key={u.id} className="hover:bg-gray-50 dark:hover:bg-gray-900">
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <Avatar src={u.profile_pic} name={u.name || u.email} size={32} profileDetails={{ name: u.name || u.email, email: u.email, role: roles?.find(r => r.id === (u.roleId || u.role))?.name || u.role }} />
                    <div className="font-medium text-gray-900 dark:text-gray-100">{u.name || '-'}</div>
                  </div>
                </td>
                <td className="p-4 text-gray-700 dark:text-gray-300">{u.email || '-'}</td>
                <td className="p-4">{renderRole(u.role)}</td>
                <td className="p-4 text-gray-700 dark:text-gray-300">
                  {(partnersAll?.find((p) => p.id === (u.parent_id || u.tenantId)) || {}).name || '-'}
                </td>
                <td className="p-4 text-gray-700 dark:text-gray-300">
                  {(tenantsAll?.find((t) => t.id === u.tenantId) || {}).name || '-'}
                </td>
                <td className="p-4 text-xs text-gray-500 dark:text-gray-400">
                  {formatDate(u.createdAt || u.created_at)}
                </td>
                <td className="p-4">{renderStatus(u.status)}</td>
                <td className="p-4 text-center">
                  {canManage && (
                    <Button variant="secondary" onClick={() => openManage(u)}>Manage</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>

      <Modal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingUser(null); }}
        title={editingUser ? 'Manage User' : 'Add New User'}
      >
        <UserForm
          user={editingUser}
          me={me}
          roles={roles || []}
          tenants={tenantsScoped}
          partners={partnersScoped}
          onSave={handleSave}
          onCancel={() => { setIsModalOpen(false); setEditingUser(null); }}
          policyCache={policyCache}
          setPolicyCache={setPolicyCache}
        />
      </Modal>
    </StandardPageLayout>
  );
};

export default UsersPage;
